/**
 * MultipartUploader - Orchestrates S3 multipart uploads for large files
 * 
 * Splits files into chunks and uploads them in parallel for faster uploads.
 * Includes automatic retry with exponential backoff and progress tracking.
 * 
 * @module MultipartUploader
 */

import * as fileMetadataApi from './fileMetadataApi';

// Constants
const DEFAULT_PART_SIZE = 10 * 1024 * 1024; // 10MB
const MIN_PART_SIZE = 5 * 1024 * 1024; // 5MB (S3 minimum)
const MAX_PART_SIZE = 100 * 1024 * 1024; // 100MB
const DEFAULT_CONCURRENCY = 4;
const DEFAULT_MAX_RETRIES = 3;
const BASE_RETRY_DELAY = 1000; // 1 second
const PART_UPLOAD_TIMEOUT = 120000; // 2 minutes per part — if no progress for this long, abort
const STALL_TIMEOUT = 30000; // 30 seconds with no progress = stalled

/**
 * MultipartUploader class for handling large file uploads
 */
class MultipartUploader {
  /**
   * Create a MultipartUploader instance
   * @param {Object} options - Configuration options
   * @param {File} options.file - The file to upload
   * @param {string} [options.parentFolderId='ROOT'] - Parent folder ID
   * @param {Function} [options.onProgress] - Progress callback (0-100)
   * @param {number} [options.partSize=10MB] - Size of each part in bytes
   * @param {number} [options.concurrency=4] - Number of concurrent uploads
   * @param {number} [options.maxRetries=3] - Maximum retry attempts per part
   */
  constructor(options) {
    if (!options.file) {
      throw new Error('File is required');
    }

    this.file = options.file;
    this.parentFolderId = options.parentFolderId || 'ROOT';
    this.onProgress = options.onProgress || (() => {});
    this.partSize = this._validatePartSize(options.partSize || DEFAULT_PART_SIZE);
    this.concurrency = options.concurrency || DEFAULT_CONCURRENCY;
    this.maxRetries = options.maxRetries || DEFAULT_MAX_RETRIES;

    // Internal state
    this.itemId = null;
    this.uploadId = null;
    this.s3Key = null;
    this.parts = [];
    this.uploadedBytes = 0;
    this.aborted = false;
    this.abortController = new AbortController();
  }

  /**
   * Validate and normalize part size
   * @param {number} size - Requested part size
   * @returns {number} Valid part size
   */
  _validatePartSize(size) {
    if (size < MIN_PART_SIZE) return MIN_PART_SIZE;
    if (size > MAX_PART_SIZE) return MAX_PART_SIZE;
    return size;
  }

  /**
   * Calculate chunks for the file
   * @returns {Array<{partNumber: number, start: number, end: number, size: number}>}
   */
  _calculateChunks() {
    const chunks = [];
    const fileSize = this.file.size;
    let offset = 0;
    let partNumber = 1;

    while (offset < fileSize) {
      const start = offset;
      const end = Math.min(offset + this.partSize, fileSize);
      const size = end - start;

      chunks.push({
        partNumber,
        start,
        end,
        size
      });

      offset = end;
      partNumber++;
    }

    return chunks;
  }

  /**
   * Upload the file using multipart upload
   * @returns {Promise<Object>} The completed item
   */
  async upload() {
    // Warn user if they try to close the tab during upload
    const beforeUnloadHandler = (e) => {
      e.preventDefault();
      e.returnValue = 'Upload in progress. If you leave, the upload will fail.';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);

    try {
      // Step 1: Initiate multipart upload
      const initResult = await this._initiate();
      this.itemId = initResult.itemId;
      this.uploadId = initResult.uploadId;
      this.s3Key = initResult.s3Key;

      // Step 2: Upload all parts in parallel
      const parts = await this._uploadParts();

      // Step 3: Complete the upload
      const result = await this._complete(parts);
      return result.item;
    } catch (error) {
      // Abort on failure to clean up S3 resources
      if (this.uploadId && !this.aborted) {
        await this._abort().catch(abortError => {
          console.error('Error aborting multipart upload:', abortError);
        });
      }
      throw error;
    } finally {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    }
  }

  /**
   * Initiate the multipart upload
   * @returns {Promise<{itemId: string, uploadId: string, s3Key: string}>}
   */
  async _initiate() {
    return fileMetadataApi.createMultipartUpload({
      fileName: this.file.name,
      mimeType: this.file.type || 'application/octet-stream',
      size: this.file.size,
      parentFolderId: this.parentFolderId
    });
  }

  /**
   * Upload all parts with concurrency control.
   * 
   * Uses Promise.all with a concurrency pool — the same pattern used by the
   * AWS S3 multipart upload reference implementation. Each chunk is wrapped
   * in a function that waits for a pool slot before starting. Promise.all
   * rejects immediately if any part fails, so errors propagate cleanly with
   * no race conditions.
   * 
   * @returns {Promise<Array<{partNumber: number, etag: string}>>}
   */
  async _uploadParts() {
    const chunks = this._calculateChunks();

    // Track bytes per part for progress reporting
    const partProgress = new Map();
    chunks.forEach(chunk => partProgress.set(chunk.partNumber, 0));

    const updateProgress = () => {
      const totalUploaded = Array.from(partProgress.values()).reduce((a, b) => a + b, 0);
      const percentage = Math.round((totalUploaded / this.file.size) * 100);
      this.onProgress(Math.min(percentage, 100));
    };

    // Concurrency pool: at most `this.concurrency` uploads run at once.
    // Each call to `acquireSlot` returns a promise that resolves when a
    // slot is free. Callers release their slot by calling `releaseSlot`.
    let activeCount = 0;
    const waitQueue = [];

    const acquireSlot = () => {
      if (activeCount < this.concurrency) {
        activeCount++;
        return Promise.resolve();
      }
      return new Promise(resolve => waitQueue.push(resolve));
    };

    const releaseSlot = () => {
      if (waitQueue.length > 0) {
        const next = waitQueue.shift();
        next();
      } else {
        activeCount--;
      }
    };

    // Create one promise per chunk. Each waits for a pool slot, uploads
    // with retry (via _uploadPart), then releases the slot. Because every
    // chunk is represented in the Promise.all array, a failure in any
    // chunk rejects the whole batch — no silent swallowing of errors.
    const partPromises = chunks.map(async (chunk) => {
      await acquireSlot();

      try {
        if (this.aborted) {
          throw new Error('Upload aborted');
        }

        const result = await this._uploadPart(chunk, (bytes) => {
          partProgress.set(chunk.partNumber, bytes);
          updateProgress();
        });

        return result;
      } finally {
        releaseSlot();
      }
    });

    // Promise.all fails fast: if any part fails (after retries in
    // _uploadPart), the whole upload fails immediately.
    const completedParts = await Promise.all(partPromises);

    // Sort by part number for the CompleteMultipartUpload call
    return completedParts.sort((a, b) => a.partNumber - b.partNumber);
  }

  /**
   * Upload a single part with retry logic
   * @param {Object} chunk - Chunk info
   * @param {Function} onPartProgress - Progress callback for this part
   * @returns {Promise<{partNumber: number, etag: string}>}
   */
  async _uploadPart(chunk, onPartProgress) {
    let lastError;

    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      if (this.aborted) {
        throw new Error('Upload aborted');
      }

      try {
        // Get presigned URL for this part
        const { uploadUrl } = await fileMetadataApi.getPartUploadUrl(
          this.itemId,
          this.uploadId,
          chunk.partNumber
        );

        // Upload the chunk
        const blob = this.file.slice(chunk.start, chunk.end);
        const etag = await this._uploadToS3(uploadUrl, blob, onPartProgress);

        return {
          partNumber: chunk.partNumber,
          etag
        };
      } catch (error) {
        lastError = error;
        console.warn(`Part ${chunk.partNumber} upload failed (attempt ${attempt + 1}/${this.maxRetries + 1}):`, error.message);

        if (attempt < this.maxRetries) {
          // Exponential backoff
          const delay = BASE_RETRY_DELAY * Math.pow(2, attempt);
          await this._sleep(delay);
        }
      }
    }

    throw new Error(`Part ${chunk.partNumber} failed after ${this.maxRetries + 1} attempts: ${lastError.message}`);
  }

  /**
   * Upload blob to S3 using presigned URL
   * @param {string} url - Presigned PUT URL
   * @param {Blob} blob - Data to upload
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<string>} ETag from S3
   */
  async _uploadToS3(url, blob, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let settled = false;
      let lastProgressTime = Date.now();

      // Stall detection: if no upload progress for STALL_TIMEOUT, abort
      const stallCheck = setInterval(() => {
        if (Date.now() - lastProgressTime > STALL_TIMEOUT) {
          clearInterval(stallCheck);
          if (!settled) {
            settled = true;
            xhr.abort();
            reject(new Error(`Upload stalled — no progress for ${STALL_TIMEOUT / 1000}s`));
          }
        }
      }, 5000);

      // Hard timeout: if the entire part upload takes too long, abort
      const hardTimeout = setTimeout(() => {
        clearInterval(stallCheck);
        if (!settled) {
          settled = true;
          xhr.abort();
          reject(new Error(`Upload timed out after ${PART_UPLOAD_TIMEOUT / 1000}s`));
        }
      }, PART_UPLOAD_TIMEOUT);

      const cleanup = () => {
        clearInterval(stallCheck);
        clearTimeout(hardTimeout);
      };

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          lastProgressTime = Date.now();
          onProgress(event.loaded);
        }
      });

      xhr.addEventListener('load', () => {
        cleanup();
        if (settled) return;
        settled = true;
        if (xhr.status >= 200 && xhr.status < 300) {
          // Get ETag from response headers
          const etag = xhr.getResponseHeader('ETag');
          if (!etag) {
            reject(new Error('No ETag in response'));
            return;
          }
          resolve(etag);
        } else {
          reject(new Error(`S3 upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        cleanup();
        if (settled) return;
        settled = true;
        reject(new Error('S3 upload network error'));
      });

      xhr.addEventListener('abort', () => {
        cleanup();
        if (settled) return;
        settled = true;
        reject(new Error('S3 upload aborted'));
      });

      // Listen for abort signal
      this.abortController.signal.addEventListener('abort', () => {
        xhr.abort();
      });

      xhr.open('PUT', url);
      xhr.send(blob);
    });
  }

  /**
   * Complete the multipart upload
   * @param {Array<{partNumber: number, etag: string}>} parts - Uploaded parts
   * @returns {Promise<{item: Object}>}
   */
  async _complete(parts) {
    return fileMetadataApi.completeMultipartUpload(this.itemId, this.uploadId, parts);
  }

  /**
   * Abort the multipart upload
   * @returns {Promise<void>}
   */
  async _abort() {
    if (!this.uploadId) return;
    
    this.aborted = true;
    this.abortController.abort();
    
    await fileMetadataApi.abortMultipartUpload(this.itemId, this.uploadId);
    
    // Clean up the PENDING DynamoDB record
    if (this.itemId) {
      try {
        await fileMetadataApi.deleteItem(this.itemId);
      } catch (cleanupError) {
        console.error('Failed to clean up PENDING record after abort:', cleanupError);
      }
    }
  }

  /**
   * Cancel the upload (public method)
   * @returns {Promise<void>}
   */
  async cancel() {
    return this._abort();
  }

  /**
   * Sleep for a given duration
   * @param {number} ms - Milliseconds to sleep
   * @returns {Promise<void>}
   */
  _sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Export constants for testing
export const CONSTANTS = {
  DEFAULT_PART_SIZE,
  MIN_PART_SIZE,
  MAX_PART_SIZE,
  DEFAULT_CONCURRENCY,
  DEFAULT_MAX_RETRIES,
  BASE_RETRY_DELAY,
  PART_UPLOAD_TIMEOUT,
  STALL_TIMEOUT
};

export default MultipartUploader;
